'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useSystems } from '@/api/queries/useSystems';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  cn,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger
} from '@bliks-io/ui';
import { useFilteredItems } from './useFilteredItems';
import type { EntitySelectFilter } from './select.types';

interface SystemSelectProps {
  value: string | string[] | undefined;
  onChange: (value: string | string[] | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Enable multi-select mode
   */
  multiple?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /**
   * Allow clearing the selection (useful for optional fields)
   */
  allowClear?: boolean;
  /**
   * Custom label for the clear/all option
   */
  clearLabel?: string;
  /**
   * Optional callback for custom clear behavior. If not provided, defaults to onChange(undefined)
   */
  onClear?: () => void;
  /**
   * Client-side filter function to apply before internal search filter.
   * Useful for filtering systems by custom criteria (e.g., excluding already selected systems).
   */
  filter?: EntitySelectFilter<NonNullable<ReturnType<typeof useSystems>['data']>[number]>;
}

/**
 * Standalone SystemSelect component
 * Supports both single and multi-select modes with search functionality
 *
 * This is the base component. For form field integration, create FormFieldSystemSelect.
 * For React Hook Form, create FormSystemSelect.
 */
export const SystemSelect = React.forwardRef<HTMLButtonElement, SystemSelectProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled = false,
      multiple = false,
      className,
      id,
      name,
      allowClear = false,
      clearLabel,
      onClear,
      filter
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { data: systems, isLoading, isError } = useSystems();

    const [searchQuery, setSearchQuery] = React.useState('');

    const fieldId = id || name;
    const isDisabled = disabled || isLoading || isError || !systems || systems.length === 0;

    // Determine placeholder text based on loading/error states
    let placeholderText = placeholder || t('systemSelect.placeholder');
    if (isLoading) {
      placeholderText = t('systemSelect.loading');
    } else if (isError || !systems) {
      placeholderText = t('systemSelect.error');
    } else if (systems.length === 0) {
      placeholderText = t('systemSelect.noSystems');
    }

    // Search filter for systems (by name and description)
    const searchFilter = React.useCallback(
      (system: NonNullable<typeof systems>[number]) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const name = system.name?.toLowerCase() || '';
        const description = system.description?.toLowerCase() || '';
        return name.includes(q) || description.includes(q);
      },
      [searchQuery]
    );

    // Two-stage filtering: external filter + search
    const filteredSystems = useFilteredItems(systems, filter, searchFilter);

    // Get selected systems for display
    const selectedSystems = React.useMemo(() => {
      if (!systems || !value) return [];
      const selectedIds = Array.isArray(value) ? value : [value];
      return systems.filter((system) => selectedIds.includes(system._id));
    }, [systems, value]);

    // Extract logo URL from system
    const getSystemLogoUrl = React.useCallback((system: NonNullable<typeof systems>[number]) => {
      if (!system.logo) return undefined;
      if (system.logo.type === 'svg') {
        return system.logo.svg;
      }
      return system.logo.thumbnail?.webp || system.logo.thumbnail?.png;
    }, []);

    // Render trigger content
    const triggerContent = React.useMemo(() => {
      if (selectedSystems.length === 0) {
        return null;
      }

      // Single selection or first item in multi-select with only one item
      if (!multiple || selectedSystems.length === 1) {
        const system = selectedSystems[0];
        const logoUrl = getSystemLogoUrl(system);

        return (
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={system.name}
                className="h-5 max-w-[80px] w-auto object-contain"
              />
            ) : (
              <ENTITY_ICONS.system className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="truncate">{system.name}</span>
          </div>
        );
      }

      // Multiple systems selected
      return (
        <span className="truncate">
          {t('systemSelect.systemsSelected', { count: selectedSystems.length })}
        </span>
      );
    }, [selectedSystems, multiple, t, getSystemLogoUrl]);

    // Handle clear option selection
    const handleClearSelect = React.useCallback(() => {
      if (onClear) {
        onClear();
      } else {
        onChange(multiple ? [] : undefined);
      }
    }, [onClear, onChange, multiple]);

    return (
      <Combobox
        value={value}
        onValueChange={onChange}
        disabled={isDisabled}
        multiple={multiple}
        onClear={onClear}
      >
        <ComboboxTrigger
          ref={ref}
          id={fieldId}
          placeholder={placeholderText}
          allowClear={allowClear}
          className={className}
        >
          {triggerContent}
        </ComboboxTrigger>
        <ComboboxContent>
          <ComboboxInput
            placeholder={t('systemSelect.search')}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <ComboboxList>
            <ComboboxEmpty>{t('systemSelect.noResults')}</ComboboxEmpty>
            {allowClear && (
              <ComboboxItem value="__clear__" onSelect={handleClearSelect} className="gap-2">
                <span className={cn('flex-1', selectedSystems.length === 0 && 'font-semibold')}>
                  {clearLabel || t('systemSelect.noSystem')}
                </span>
                <Check
                  className={cn(
                    'h-4 w-4 shrink-0',
                    selectedSystems.length === 0 ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </ComboboxItem>
            )}
            {filteredSystems.map((system) => {
              const logoUrl = getSystemLogoUrl(system);
              return (
                <ComboboxItem key={system._id} value={system._id} className="gap-2">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={system.name}
                      className="h-5 max-w-[80px] w-auto object-contain"
                    />
                  ) : (
                    <ENTITY_ICONS.system className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="flex-1 truncate">{system.name}</span>
                </ComboboxItem>
              );
            })}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  }
);

SystemSelect.displayName = 'SystemSelect';
